import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiErrorException } from "@/types/api.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";
import { apiJobArchivingReasons } from "@/api/recruit/job-archiving-reason.api.ts";
import { ApiJobArchivingReasonPayload } from "@/types/recruit/job-archiving-reason.types.ts";

const jobArchivingReasonsKeys = createQueryKeys("jobArchivingReasons", {
  all: null,
});

export const useJobArchivingReasons = () => {
  return useQuery({
    ...jobArchivingReasonsKeys.all,
    queryFn: async () => {
      const result = await apiJobArchivingReasons.getAll();
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    staleTime: 60 * 1000,
  });
};

export interface SaveParams {
  id?: number;
  title: string;
}

export const useJobArchivingReasonSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async ({ id, title }: SaveParams) => {
      const payload: ApiJobArchivingReasonPayload = {
        title: title,
      };
      const result = id
        ? await apiJobArchivingReasons.update(id, payload)
        : await apiJobArchivingReasons.create(payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: jobArchivingReasonsKeys.all.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useJobArchivingReasonDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await apiJobArchivingReasons.delete(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: jobArchivingReasonsKeys.all.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};
